import React, { useState } from "react";
import Axios from "axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Config from "../../Config";
import { serverError } from "../../../utils/password";
import { AccountLayout, BackToSignIn, PasswordForm, StatusBadge } from "./shared";

/**
 * /reset-password?reset_password_token=… — arrivée depuis le mail.
 * PUT {authURL}/auth/password { password, password_confirmation, reset_password_token }.
 * Sans jeton, le lien est inutilisable : on renvoie vers une nouvelle demande.
 */
const ResetPassword = ({ location }) => {
  const { t } = useTranslation();
  const [token] = useState(() => new URLSearchParams(location?.search).get("reset_password_token"));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (password, confirmation) => {
    setSubmitting(true);
    setError(null);
    try {
      await Axios.put(
        `${Config.authURL}/auth/password`,
        { password, password_confirmation: confirmation, reset_password_token: token },
        Config.headers
      );
      setDone(true);
    } catch (err) {
      setError(serverError(err) || t("Network error, please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AccountLayout t={t}>
        <StatusBadge icon="i-lock" tone="warning" />
        <h1 className="wosh-auth__title">{t("This link doesn't work")}</h1>
        <p className="wosh-auth__lede">{t("It may be incomplete or already used. Ask for a new one.")}</p>
        <Link to="/forgot-password" className="wosh-auth__cta wosh-acc__cta">
          {t("Get a new link")}
        </Link>
        <BackToSignIn t={t} />
      </AccountLayout>
    );
  }

  if (done) {
    return (
      <AccountLayout t={t}>
        <StatusBadge icon="i-check" tone="success" />
        <h1 className="wosh-auth__title">{t("Password updated")}</h1>
        <p className="wosh-auth__lede">{t("You can now sign in with your new password.")}</p>
        <Link to="/sign-in" className="wosh-auth__cta wosh-acc__cta">
          {t("Sign in")}
        </Link>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout t={t}>
      <h1 className="wosh-auth__title">{t("Choose a new password")}</h1>
      <p className="wosh-auth__lede">{t("At least 6 characters.")}</p>
      {error && <div className="wosh-auth__alert">{error}</div>}
      <PasswordForm t={t} cta={t("Save the password")} submitting={submitting} onSubmit={submit} />
      <BackToSignIn t={t} />
    </AccountLayout>
  );
};

export default ResetPassword;
